import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { ProgressCircular } from 'react-rainbow-components';
import { RecordField } from '@rainbow-modules/record';
import Card from 'components/Card';
import AlertCircle from 'components/icons/alertCircle';
import ClockIcon from 'components/icons/clock';
import UserAvatar from 'components/UserAvatar';
import ListTile from 'components/ListTile';
import {
    GREY_100, GREY_400, GREY_50, GREY_500, GREY_800, WHITE,
} from '../../../../constants';

export const CardContainer = styled(Card)<{ isStuck?: boolean }>`
    display: flex;
    padding: 0.875rem 0 0.25rem 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.625rem;
    align-self: stretch;
    border-radius: 1rem;
    background: #FFF;
    flex-shrink: 0;
    border: 2px solid ${GREY_100};
    box-shadow: none !important;

    ${(props) => props.isStuck && css`
        border: 2px solid #FADABB;        
    `};
`;

export const CardHeader = styled.div`
    display: flex;
    align-items: flex-start;
    padding: 0 1rem;
    gap: 0.75rem;
    align-self: stretch;
`;

export const CardHeaderContent = styled.div`
    display: flex;
    flex-direction: column;
    align-self: stretch;
`;

export const CardBody = styled.div`
    display: flex;
    flex-direction: column;
    align-self: stretch;
    padding: 0 1rem;
    gap: 0.375rem;
`;

export const CardFooter = styled.div`
    display: flex;
    padding: 0.25rem 0.5rem 0 1.125rem;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    box-sizing: border-box;
    border-top: 1px solid #F5F5F5;
    height: 3rem;
`;

export const CardActions = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
`;

export const CardTitle = styled(Link)`
    font-size: 1rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1.25rem;
    text-align: left;
    padding: 0;
    margin: 0;
    color: ${GREY_800};
    background: none;
    border: none;
    
    &:hover,
    &:focus,
    &:active {
        color: ${GREY_800}; 
    }
    
    &:hover {
        text-decoration: underline;
    }
`;

export const LastUpdateDateContainer = styled.div`
    display: flex;
    height: 1.5rem;
    justify-content: start;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1.375rem; 
    margin-top: 0.5rem;
    color: #A5A2AD;    
`;

export const StyledAlertCircle = styled(AlertCircle)`
    transform: rotate(180deg);
    color: #C18245;
`;

export const StyledClockIcon = styled(ClockIcon)`
    width: 1.5rem !important;
    height: 1.5rem !important;
    color: #88888D;
`;

export const Score = styled(ProgressCircular)`
    min-width: 1.75rem;
    width: 1.75rem;
    height: 1.75rem;
    flex-shrink: 0;

    svg {
        width: 1.6rem;
        height: 1.75rem;
        flex-shrink: 0;
        overflow: visible !important;
        
        circle {
            stroke-width: 4px;
        }
    }

    & > h1 {
        font-size: 1px;
        color: ${WHITE};
        &::after {
            color: ${GREY_400};
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            text-align: center;
            font-size: 0.625rem;
            font-style: normal;
            font-weight: 700;
            line-height: 1.375rem;

            ${(props) => props.variant && css`
                content: '${props.value}';
            `}
        }
    }
    
    ${(props) => !props.variant && css`
        color: ${GREY_400};
        > h1 {
            color: ${WHITE};
            ::after {
                color: ${GREY_400};
                content: '-';
                position: absolute;
                left: 0;
                font-size: 0.625rem;
                width: 100%;
                text-align: center;
            }
        }
    `};
`;

export const PriceLabel = styled.span`
    font-size: 0.8125rem;
    font-style: normal;
    font-weight: 700;
    line-height: 0.875rem;
`;

export const OwnerAvatar = styled(UserAvatar)`
    width: 1.25rem;
    height: 1.25rem;
    margin-left: 0.125rem;

    svg {
        width: 0.875rem;
        height: 0.875rem;
    }

    & abbr {
        font-size: 0.7rem;
    }
`;

export const StyledListTile = styled(ListTile)`
    & > div ~ div > span {
        color: #AFAFB3;
        font-size: 13px;
    }
`;

export const PipelineInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-self: stretch;
    width: 100%;
    flex: 1;
    background: ${GREY_50}66;
    padding: 0.325rem 0.25rem;
    border-radius: 0.5rem;
`;

export const StyledRecordField = styled(RecordField)<{ wrap?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    overflow: hidden;
    text-overflow: ellipsis;

    > span {
        font-size: 0.75rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1rem;
        color: ${GREY_500};
        flex-grow: 0;
    }
    
    > span:first-child {
        font-weight: 600;
        white-space: nowrap;
        color: ${GREY_500};
        line-height: 1rem;
        flex-grow: 0;
    }

    > span:last-child {
        overflow: hidden;
        color: ${GREY_500};
        text-overflow: ellipsis;
        flex: 1;
            
        > div {
            flex: 1;
        }
    }

    ${(props) => props.wrap && css`
        flex-direction: column;
        flex: 1;
        gap: 0;
        align-items: stretch;

        > span {
            align-self: stretch;
            flex: 1 1 100%;
        }
    `};
`;
